import { captureException } from "@sentry/react"
import {
  Button,
  CircularProgress,
  Divider,
  TextField,
  toast,
  Tooltip,
  Typography,
} from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { Product } from "api/resources/products/types"
import clsx from "clsx"
import AsyncBuilder from "components/AsyncBuilder"
import Card from "components/Card"
import PageContainer from "components/PageContainer"
import CpatLogo from "features/Cpat/assets/cpat-mini-logo.webp"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useSearchParams } from "react-router-dom"
import { getPlatformURL, mapErrorsV2 } from "utils/helpers"
import useFormatPrice from "utils/hooks/useFormatPrice"

import AddAddress from "./AddAddress"
import { useCPATPayment } from "./useCPATPayment"

type Props = {
  productSlug: string
}

type Promo = {
  code: string
}

type Price = {
  currency: {
    code: string
    symbol: string
  }
  itemPrice: number
  finalAmount: number
  discountAmount: number

  taxes: { amount: number; name: string }[]
}

const getPurchaseDetails = (
  product: Product | undefined,
  price: Price | undefined | null
) => {
  if (!product || !price) return null

  const details = {
    itemName: product.name,
    amount: price.finalAmount,
    currency: price.currency,
  }

  return details
}

const CartlessPaymentFlow = ({ productSlug }: Props) => {
  const [billingAddressAdded, setBillingAddressAdded] = useState(false)
  const [price, setPrice] = useState<Price | null>()
  const [promo, setPromo] = useState<Promo | null>(null)

  const product = useQuery({
    queryFn: () =>
      api.products.getProduct({
        params: {
          fields: ["prices"],
        },
        urlParams: { productSlug },
      }),
    queryKey: ["product", productSlug],
  })

  const {
    register,
    handleSubmit,
    reset,
    setError,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<{ promocode: string }>()

  const onSubmit = handleSubmit(async data => {
    await applyPromocode(payment.orderId || "", data.promocode)
  })

  useEffect(() => {
    if (product.isSuccess) {
      if (product.data.prices == null) {
        captureException(
          new Error("Product prices is null in cartless payment flow"),
          scope => {
            scope.setExtras({
              productData: product.data,
            })
            return scope
          }
        )
        return
      }
      setPrice({
        currency: product.data.prices.currency,
        itemPrice: product.data.prices.price,
        finalAmount: product.data.prices.price,
        discountAmount: 0,
        taxes: [],
      })

      const code = searchParams.get("promo") || searchParams.get("promocode")
      if (code) setValue("promocode", code)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.isSuccess])

  const payment = useCPATPayment({
    purchaseDetails: getPurchaseDetails(product.data, price),
  })

  const [searchParams] = useSearchParams()

  const applyPromocode = async (orderId: string, promocode: string | null) => {
    try {
      const res = await api.orders.applyPromocode({
        urlParams: {
          orderId,
        },
        data: {
          promoCode: promocode,
        },
      })
      setPrice({
        itemPrice: res.itemTotal,
        finalAmount: res.finalAmount,
        discountAmount: res.discountAmount,
        taxes: res.taxes,
        currency: res.currency,
      })

      if (promocode) {
        setPromo({ code: promocode })
        toast.success("Promocode applied!")
      } else {
        reset()
        setPromo(null)
        toast.info("Promocode removed")
      }
    } catch (e) {
      mapErrorsV2(e, { setter: setError, fields: [["promoCode", "promocode"]] })
    }
  }

  const formatPrice = useFormatPrice()

  const priceToPay = price
    ? `${price.currency.symbol}${formatPrice(price.finalAmount)}`
    : ""

  const canProceed = billingAddressAdded && payment.orderId != null

  return (
    <div>
      {/* Stripe Checkout Form */}
      {payment.stripePaymentElement}

      <PageContainer>
        <Typography variant="title2" className="my-3">
          Complete Payment
        </Typography>

        {product.isLoading && (
          <div className="flex items-center justify-center pt-5">
            <CircularProgress />
          </div>
        )}
        {product.data && (
          <div className="mb-2 flex w-full flex-col md:flex-row">
            <div className="md:me-3 md:w-1/2">
              <Card padding={2} className="rounded-xl">
                <div className="flex grow flex-col sm:flex-row sm:items-center">
                  <img
                    alt="item"
                    src={CpatLogo}
                    className="mb-2 size-10 shrink-0 rounded-xl border border-surface-100 sm:mb-0 sm:me-2"
                  />
                  <div>
                    <Typography variant="strong">
                      {product.data.name}
                    </Typography>
                  </div>
                </div>
              </Card>

              <AddAddress
                onAdd={async addr => {
                  const order = await payment.createOrder(
                    addr.uuid,
                    productSlug
                  )

                  if (order) {
                    setPrice({
                      itemPrice: order.itemTotal,
                      finalAmount: order.finalAmount,
                      discountAmount: order.discountAmount,
                      taxes: order.taxes,
                      currency: order.currency,
                    })
                    setBillingAddressAdded(true)

                    const code = getValues("promocode") || order.promoCode
                    if (code) {
                      setValue("promocode", code)
                      await applyPromocode(order.id, getValues("promocode"))
                    }
                  }
                }}
                onChangeClicked={() => {
                  setBillingAddressAdded(false)
                }}
              />
            </div>
            <div className="mb-10 mt-3 flex flex-col md:mt-0 md:w-1/2">
              {promo !== null ? (
                <div className="flex items-center justify-between rounded-2xl border border-surface-200 bg-[#F7F7F7] px-2 py-1.25">
                  <Typography variant="strong">{promo.code}</Typography>
                  <Button
                    variant="text"
                    onClick={() => applyPromocode(payment.orderId || "", null)}
                    color="critical"
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <Tooltip
                  enabled={!canProceed}
                  content="Please add a billing address first"
                >
                  <form onSubmit={onSubmit}>
                    <div className="flex items-center justify-between rounded-2xl border border-surface-200 bg-[#F7F7F7] px-2 py-1.25">
                      <TextField
                        errors={errors.promocode?.message}
                        helperText={errors.promocode?.message}
                        className="me-2"
                        placeholder="Promocode"
                        {...register("promocode", {
                          required: "Required",
                        })}
                        disabled={!canProceed}
                      />
                      <Button
                        variant="text"
                        type="submit"
                        disabled={!canProceed}
                      >
                        Apply
                      </Button>
                    </div>
                  </form>
                </Tooltip>
              )}
              {price && (
                <div className="mt-2 rounded-2xl border border-surface-200 bg-[#F7F7F7] px-2 py-1.25">
                  {[
                    { name: "Item", amount: price.itemPrice },
                    {
                      name: "Discount",
                      amount: price.discountAmount,
                    },
                    {
                      name: "Taxes",
                      amount: price.taxes.reduce((a, b) => a + b.amount, 0),
                    },
                  ]
                    .filter(item => item.amount > 0)
                    .map((item, idx) => (
                      <div
                        key={idx}
                        className="mb-2 flex items-center justify-between"
                      >
                        <Typography variant="strong">{item.name}</Typography>
                        <Typography
                          variant="strong"
                          className={clsx({
                            "text-success-500": item.name === "Discount",
                            "text-onSurface-900": item.name !== "Discount",
                          })}
                        >
                          {item.name === "Discount" && "-"}
                          {price.currency.symbol}
                          {formatPrice(item.amount)}
                        </Typography>
                      </div>
                    ))}
                  <Divider weight="light" className="bg-onSurface-200" />
                  <span className="mt-1 flex items-center justify-between">
                    <span className="flex items-center">
                      <Typography variant="strong" className="me-0.5">
                        Total
                      </Typography>
                    </span>
                    <Typography variant="title2">{priceToPay}</Typography>
                  </span>
                </div>
              )}
              <Tooltip
                content="Please add a billing address first"
                enabled={!canProceed}
              >
                <div>
                  <AsyncBuilder
                    handler={() => payment.checkout(promo?.code || null)}
                    render={({ loading, onClick }) => (
                      <Button
                        color="black"
                        loading={loading}
                        onClick={onClick}
                        variant="filled"
                        className="mt-2 w-full"
                        disabled={!canProceed}
                      >
                        Pay {priceToPay}
                      </Button>
                    )}
                  />
                </div>
              </Tooltip>
              <Typography
                variant="smallBody"
                className="mt-1 w-4/5 self-center text-center text-secondary-500"
              >
                By confirming this purchase, I agree to the
                <a
                  className="text-interactive-500"
                  href={getPlatformURL("suraasa", "/terms-of-use")}
                  target="_blank"
                  rel="noreferrer"
                >
                  {" "}
                  terms of use
                </a>{" "}
                and
                <a
                  className="text-interactive-500"
                  href={getPlatformURL("suraasa", "/privacy-policy")}
                  target="_blank"
                  rel="noreferrer"
                >
                  {" "}
                  privacy policy.
                </a>
              </Typography>
            </div>
          </div>
        )}
      </PageContainer>
    </div>
  )
}

export default CartlessPaymentFlow
